<template>
    <Head title="Welcome to Nadia" />
    <Transition name="fade" mode="out-in" appear>
        <div
            class="full-height-dynamic safe-area-top-min relative flex h-screen w-full flex-col items-center overflow-y-auto px-3 pb-3 pt-3 md:items-stretch md:px-6 md:pb-6 md:pt-6">
            <Bubbles direction="bottom" :is-footer="true" />

            <!-- Main content area -->
            <div class="flex w-full flex-1 flex-col items-center justify-center gap-4 md:order-1">
                <Transition name="slide-fade" mode="out-in">
                    <!-- Step 1: Welcome -->
                    <div v-if="currentStep === 0" key="welcome" class="flex w-full flex-col items-center gap-8">
                        <div class="w-16 h-16">
                            <CoachingModeMarble />
                        </div>
                        <div class="text-center leading-tight">
                            <div class="max-w-2xl display-medium text-balance mb-4">
                                <span class="gradient-text">Hi{{ firstName ? ` ${firstName}` : "" }}, I'm Nadia.</span>
                                <br />I'm an AI coach powered by context.
                            </div>
                            <p class="text-valence-grey-750 body md:display-serif max-w-xl mx-auto text-pretty">
                                Let me walk you through the four context types I use to build a living model of your world.
                            </p>
                        </div>

                        <BaseButton theme="primary" class="w-60 leading-6 body-small" @click="currentStep = 1"> Show me </BaseButton>
                    </div>

                    <!-- Steps 2-5: Individual context dimension screens -->
                    <div v-else-if="currentStep >= 1 && currentStep <= 4" key="dimensions" class="flex w-full max-w-4xl flex-col items-center gap-8">
                        <div class="flex w-full flex-col md:flex-row items-center gap-8 md:gap-12">
                            <!-- Left: Context card -->
                            <Transition name="card-fade" mode="out-in">
                                <div :key="`card-${currentStep}`" class="flex flex-col items-center gap-4 md:w-1/3">
                                    <div class="flex h-16 w-16 items-center justify-center rounded-2xl" :class="contextDimensions[currentStep - 1].bgClass">
                                        <i :class="`bi ${contextDimensions[currentStep - 1].icon} text-3xl ${contextDimensions[currentStep - 1].textClass}`" aria-hidden="true"></i>
                                    </div>
                                    <h2 class="display-small text-gray-900 text-center">{{ contextDimensions[currentStep - 1].title }}</h2>
                                    <p class="body-small text-gray-600 text-center text-pretty max-w-xs">
                                        {{ contextDimensions[currentStep - 1].description }}
                                    </p>
                                    <div class="flex items-center gap-2 mt-2">
                                        <div
                                            v-for="(dim, idx) in contextDimensions"
                                            :key="idx"
                                            class="h-2 rounded-full transition-all duration-300"
                                            :class="[idx === currentStep - 1 ? 'w-6' : 'w-2', idx <= currentStep - 1 ? dim.dotClass : 'bg-gray-200']" />
                                    </div>
                                </div>
                            </Transition>

                            <!-- Right: Knowledge graph -->
                            <ContextKnowledgeGraph :active-step="currentStep" />
                        </div>

                        <!-- Navigation -->
                        <div class="flex items-center justify-center gap-4">
                            <BaseButton theme="tertiary" @click="currentStep = currentStep - 1"> <i class="bi bi-arrow-left mr-1" aria-hidden="true"></i> Back </BaseButton>
                            <BaseButton v-if="currentStep < 4" theme="primary" class="w-48 leading-6 body-small" @click="currentStep = currentStep + 1"> Next </BaseButton>
                            <BaseButton v-else theme="primary" class="w-48 leading-6 body-small" @click="currentStep = 5"> See Day 1 vs Day 60 </BaseButton>
                        </div>
                    </div>

                    <!-- Step 6: Day 1 vs Day 60 -->
                    <ContextDeepDive v-else-if="currentStep === 5" key="deep-dive" @next="currentStep = 6" @back="currentStep = 4" />

                    <!-- Step 7: Experience Selection -->
                    <ExperienceSelection v-else-if="currentStep === 6" key="selection" @select="handleExperienceSelect" @back="currentStep = 5" />

                    <!-- Step 8: Session 20 Preview -->
                    <Session20Preview v-else-if="currentStep === 7 && selectedExperience === 'preloaded'" key="session20" @back="currentStep = 6" @next="currentStep = 8" />

                    <!-- Step 9: Enable Context Guide -->
                    <EnableContextGuide v-else-if="currentStep === 8 && selectedExperience === 'preloaded'" key="enable-context" @back="currentStep = 7" @start="goToOnboarding" />

                    <!-- "Kick the tires" (fresh) goes straight to /coach/onboarding — no interstitial -->

                    <!-- Step 8: Admin stub -->
                    <div v-else-if="currentStep === 7 && selectedExperience === 'admin'" key="admin-stub" class="flex flex-col items-center gap-8">
                        <div class="w-32 h-32">
                            <CoachingModeMarble size="big" />
                        </div>
                        <div class="text-center">
                            <p class="display-medium text-balance gradient-text">The admin experience is coming soon</p>
                            <p class="text-valence-grey-750 body-small mt-3">The organizational overview for HR and L&amp;D buyers will be available in a future update.</p>
                        </div>
                        <BaseButton theme="primary" class="w-75 leading-6 body-small" @click="currentStep = 6"> Back to options </BaseButton>
                    </div>
                </Transition>
            </div>

            <!-- Footer -->
            <div class="flex items-center justify-between mt-8 md:my-6 w-full">
                <div>
                    <PrivacyDialog />
                </div>
                <img :src="ColorLogo" alt="Valence" class="h-5 w-auto" />
                <div class="w-20"></div>
            </div>
        </div>
    </Transition>
    <CookieBanner />
</template>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import ColorLogo from "~img/color-logo.svg";
import BaseButton from "~vue/components/BaseButton.vue";
import Bubbles from "~vue/components/Bubbles.vue";
import CookieBanner from "~vue/components/CookieBanner.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import type { ExperienceOption } from "~vue/EvaluatorOnboarding/ExperienceSelection.vue";
import PrivacyDialog from "~vue/Onboarding/PrivacyDialog.vue";
import { logUserInteraction } from "~vue/utils/logUtils";
import { onMounted, ref } from "vue";

import { getCookie } from "../../utils";
import ContextDeepDive from "../EvaluatorOnboarding/ContextDeepDive.vue";
import ContextKnowledgeGraph from "../EvaluatorOnboarding/ContextKnowledgeGraph.vue";
import EnableContextGuide from "../EvaluatorOnboarding/EnableContextGuide.vue";
import ExperienceSelection from "../EvaluatorOnboarding/ExperienceSelection.vue";
import Session20Preview from "../EvaluatorOnboarding/Session20Preview.vue";

const props = defineProps<{
    first_name?: string;
    event_context?: { window_id: string };
}>();

const firstName = ref(props.first_name ?? "");
const currentStep = ref(0);
const selectedExperience = ref<ExperienceOption | null>(null);

const contextDimensions = [
    {
        icon: "bi-person-fill",
        title: "You",
        description: "I tailor coaching to how you best learn based on your working style and your goals.",
        bgClass: "bg-valence-pink-50",
        textClass: "text-valence-pink-600",
        dotClass: "bg-valence-pink-400",
    },
    {
        icon: "bi-people-fill",
        title: "Your relationships",
        description: "Make sure every message lands with Collaboration Intelligence \u2014 I know who\u2019s on your team and how they prefer to receive information.",
        bgClass: "bg-valence-light-blue-50",
        textClass: "text-valence-light-blue-600",
        dotClass: "bg-valence-light-blue-400",
    },
    {
        icon: "bi-calendar-week",
        title: "Your week",
        description: "I help identify key coaching moments and high-stakes meetings throughout the week so we can prepare together.",
        bgClass: "bg-valence-violet-50",
        textClass: "text-valence-violet-600",
        dotClass: "bg-valence-violet-400",
    },
    {
        icon: "bi-building",
        title: "Your organization",
        description: "Each employee gets organization-specific context in the flow of work so organization values are front and center.",
        bgClass: "bg-[#fef5e1]",
        textClass: "text-valence-gold-600",
        dotClass: "bg-valence-gold-600",
    },
];

function handleExperienceSelect(option: ExperienceOption) {
    selectedExperience.value = option;
    logUserInteraction("evaluator_experience_selected", { option });
    markEvaluatorOnboardingComplete();

    if (option === "fresh") {
        goToOnboarding();
        return;
    }
    currentStep.value = 7;
}

function markEvaluatorOnboardingComplete() {
    fetch("/coach/evaluator-onboarding/complete", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCookie("csrftoken") ?? "",
        },
    }).catch(() => {
        // Best-effort — don't block the user flow if this fails
    });
}

function goToOnboarding() {
    logUserInteraction("evaluator_start_onboarding", { experience: selectedExperience.value });
    globalThis.location.href = "/coach/onboarding";
}

onMounted(() => {
    logUserInteraction("evaluator_onboarding_viewed");
});
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.3s ease;
}

.slide-fade-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.slide-fade-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

.card-fade-enter-active,
.card-fade-leave-active {
    transition: all 0.25s ease;
}

.card-fade-enter-from {
    opacity: 0;
    transform: translateY(12px);
}

.card-fade-leave-to {
    opacity: 0;
    transform: translateY(-12px);
}
</style>
